import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useDropboxFiles } from '../../hooks/useDropboxFiles';
import { useDropboxFolders } from '../../hooks/useDropboxFolders';

interface DeleteConfirmModalProps {
  item: any;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Enterprise-grade delete confirmation modal
 * Compact, standardized design with permanent delete option
 */
const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ item, onClose, onSuccess }) => {
  const [permanent, setPermanent] = useState(false);
  const { deleteFile, loading: fileLoading } = useDropboxFiles();
  const { deleteFolder, loading: folderLoading } = useDropboxFolders();
  
  const isFolder = 'path' in item;
  const loading = fileLoading || folderLoading;

  const handleDelete = async () => {
    let result;
    if (isFolder) {
      result = await deleteFolder(item.id, permanent);
    } else {
      result = await deleteFile(item.id, permanent);
    }

    if (result) {
      onSuccess();
      onClose();
    }
  };

  return (
    <Modal 
      show={true} 
      onHide={onClose} 
      centered 
      size="sm"
      backdrop="static"
      keyboard={!loading}
    >
      <Modal.Header closeButton={!loading} className="border-bottom-0 pb-2">
        <Modal.Title className="h6 mb-0">
          <i className="fas fa-trash text-danger mr-2"></i>
          Delete {isFolder ? 'Folder' : 'File'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2 pb-3">
        <div className="text-muted mb-3" style={{ fontSize: '0.9rem' }}>
          Are you sure you want to delete{' '}
          <strong 
            className="text-dark" 
            style={{ 
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              hyphens: 'auto',
              display: 'inline-block',
              maxWidth: '100%'
            }}
            title={item.name}
          >
            {item.name} ?
          </strong>
        </div>
        <Form.Check 
          type="checkbox"
          id="permanent-delete"
          label={
            <span>
              <strong className="text-danger">Permanently delete</strong>
              <small className="d-block text-muted">Cannot be recovered</small>
            </span>
          }
          checked={permanent}
          onChange={(e) => setPermanent(e.target.checked)}
          className="mb-2"
        />
        {!permanent && (
          <small className="text-muted d-block">
            <i className="fas fa-info-circle mr-1"></i>
            Will be moved to trash and can be restored later
          </small>
        )}
      </Modal.Body>
      <Modal.Footer className="border-top-0 pt-0">
        <Button 
          variant="light" 
          size="sm"
          onClick={onClose} 
          disabled={loading}
          className="px-3"
        >
          Cancel
        </Button>
        <Button 
          variant={permanent ? 'danger' : 'warning'}
          size="sm"
          onClick={handleDelete} 
          disabled={loading}
          className="px-3"
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
              Deleting...
            </>
          ) : (
            permanent ? 'Delete Permanently' : 'Move to Trash'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteConfirmModal;
